import React, { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, Input, Spin } from "antd";
import {
  CustomerServiceOutlined,
  MailOutlined,
  MessageOutlined,
  SendOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import theme from "../themes";
import { draftCampaignMessage } from "../services/campaign.service";
import { extractTemplateVariableNames } from "../utils/templateVariables";
import { smsSegmentInfo } from "../utils/smsSegments";
import { withStopOptOut, withoutStopOptOut } from "../utils/smsStopOptOut";

type ChatItem = {
  role: "assistant" | "user";
  text: string;
};

type CampaignMessagePreviewProps = {
  value?: string;
  onChange?: (value: string) => void;
  channel: string;
  resetToken?: string;
};

const suggestionKeys = [
  "adjustSuggestUrgency",
  "adjustSuggestShorter",
  "adjustSuggestWarmer",
] as const;

const CampaignMessagePreview: React.FC<CampaignMessagePreviewProps> = ({
  value,
  onChange,
  channel,
  resetToken,
}) => {
  const { t } = useTranslation();
  const draft = String(value || "");
  const [instruction, setInstruction] = useState("");
  const [sending, setSending] = useState(false);
  const [chat, setChat] = useState<ChatItem[]>([]);
  const [appendStopOptOut, setAppendStopOptOut] = useState(true);
  const threadRef = useRef<HTMLDivElement>(null);
  const isSmsChannel = channel === "sms";

  useEffect(() => {
    const hasDraft = Boolean(String(value || "").trim());
    setChat([
      {
        role: "assistant",
        text: hasDraft
          ? t("campaignModal.create.adjustIntro")
          : t("campaignModal.create.adjustIntroEmpty"),
      },
    ]);
    setInstruction("");
    setSending(false);
    setAppendStopOptOut(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed from the draft at channel/reset time only
  }, [resetToken, channel, t]);

  useEffect(() => {
    if (!isSmsChannel || !appendStopOptOut) return;
    const next = withStopOptOut(draft);
    if (next && next !== draft) onChange?.(next);
    // Only sync when channel/reset/checkbox flips — not on every draft keystroke
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSmsChannel, appendStopOptOut, resetToken]);

  useEffect(() => {
    const node = threadRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [chat, sending]);

  const draftTitle =
    channel === "whatsapp"
      ? t("campaignModal.create.draftTitleWhatsapp")
      : channel === "email"
      ? t("campaignModal.create.draftTitleEmail")
      : t("campaignModal.create.draftTitleSms");

  const DraftIcon =
    channel === "whatsapp"
      ? WhatsAppOutlined
      : channel === "email"
      ? MailOutlined
      : MessageOutlined;

  const smsInfo = useMemo(
    () => (isSmsChannel ? smsSegmentInfo(draft) : null),
    [isSmsChannel, draft]
  );

  const variableNames = useMemo(
    () => extractTemplateVariableNames(draft),
    [draft]
  );

  const metaParts = useMemo(() => {
    const parts = [
      t("campaignModal.create.characterCount", {
        count: smsInfo ? smsInfo.characters : Array.from(draft).length,
      }),
    ];
    if (smsInfo) {
      parts.push(
        t(
          smsInfo.segments === 1
            ? "campaignModal.create.textSegment"
            : "campaignModal.create.textSegments",
          { count: smsInfo.segments }
        )
      );
    }
    if (variableNames.length === 1) {
      parts.push(
        t("campaignModal.create.variableFromList", {
          name: `{${variableNames[0]}}`,
        })
      );
    } else if (variableNames.length > 1) {
      parts.push(
        t("campaignModal.create.variablesFromList", {
          names: variableNames.map((name) => `{${name}}`).join(", "),
        })
      );
    }
    return parts;
  }, [draft, smsInfo, t, variableNames]);

  const applyDraft = (next: string) => {
    if (!isSmsChannel || !appendStopOptOut) {
      onChange?.(next);
      return;
    }
    onChange?.(withStopOptOut(next));
  };

  const handleAppendStopChange = (checked: boolean) => {
    setAppendStopOptOut(checked);
    if (checked) {
      onChange?.(withStopOptOut(draft));
    } else {
      onChange?.(withoutStopOptOut(draft));
    }
  };

  const sendInstruction = async (raw: string) => {
    const prompt = raw.trim();
    if (!prompt || sending) return;
    setInstruction("");
    setChat((prev) => [...prev, { role: "user", text: prompt }]);
    setSending(true);
    try {
      const res = await draftCampaignMessage({
        channel,
        current_message: draft,
        instruction: prompt,
      });
      const payload = res.data?.data || res.data || {};
      const nextMessage = String(payload.message || "").trim();
      const note = String(
        payload.note || t("campaignModal.create.adjustIntro")
      ).trim();
      if (!nextMessage) {
        throw new Error("empty");
      }
      applyDraft(nextMessage);
      setChat((prev) => [...prev, { role: "assistant", text: note }]);
    } catch {
      setChat((prev) => [
        ...prev,
        { role: "assistant", text: t("campaignModal.create.adjustFailed") },
      ]);
    } finally {
      setSending(false);
    }
  };

  const showSuggestions =
    chat.length === 1 && chat[0]?.role === "assistant" && !sending;

  return (
    <PreviewShell>
      <PreviewCopy>
        <PreviewTitle>{t("campaignModal.create.previewTitle")}</PreviewTitle>
        <PreviewDescription>
          {t("campaignModal.create.previewDescription")}
        </PreviewDescription>
      </PreviewCopy>
      <PreviewGrid>
        <PreviewCard>
          <CardHeader>
            <CardTitle>
              <DraftIcon />
              {draftTitle}
            </CardTitle>
            <EditableTag>{t("campaignModal.create.draftEditable")}</EditableTag>
          </CardHeader>
          <DraftArea
            value={draft}
            onChange={(event) => onChange?.(event.target.value)}
            placeholder={t("campaignModal.create.messagePlaceholder")}
            autoSize={{ minRows: 10, maxRows: 16 }}
          />
          <CardMeta>{metaParts.join(" · ")}</CardMeta>
          {isSmsChannel && (
            <StopOptOutRow>
              <StopOptOutCheckbox>
                <Checkbox
                  checked={appendStopOptOut}
                  onChange={(event) =>
                    handleAppendStopChange(event.target.checked)
                  }
                >
                  {t("campaignModal.create.appendStopOptOut")}
                </Checkbox>
              </StopOptOutCheckbox>
            </StopOptOutRow>
          )}
        </PreviewCard>
        <PreviewCard>
          <CardHeader>
            <CardTitle>
              <CustomerServiceOutlined />
              {t("campaignModal.create.adjustTitle")}
            </CardTitle>
          </CardHeader>
          <Thread ref={threadRef}>
            {chat.map((item, index) =>
              item.role === "user" ? (
                <UserBubble key={`${item.role}-${index}`}>{item.text}</UserBubble>
              ) : (
                <AgentBubble key={`${item.role}-${index}`}>{item.text}</AgentBubble>
              )
            )}
            {showSuggestions && (
              <SuggestionRow>
                {suggestionKeys.map((key) => (
                  <SuggestionChip
                    key={key}
                    type="button"
                    onClick={() =>
                      sendInstruction(t(`campaignModal.create.${key}`))
                    }
                  >
                    {t(`campaignModal.create.${key}`)}
                  </SuggestionChip>
                ))}
              </SuggestionRow>
            )}
            {sending && (
              <Typing>
                <Spin size="small" />
              </Typing>
            )}
          </Thread>
          <Composer>
            <ComposerInput
              value={instruction}
              onChange={(event) => setInstruction(event.target.value)}
              placeholder={t("campaignModal.create.adjustPlaceholder")}
              disabled={sending}
              onPressEnter={(event) => {
                if (event.shiftKey) return;
                event.preventDefault();
                sendInstruction(instruction);
              }}
            />
            <SendButton
              type="button"
              aria-label={t("campaignModal.create.adjustPlaceholder")}
              disabled={sending || !instruction.trim()}
              onClick={() => sendInstruction(instruction)}
            >
              <SendOutlined />
            </SendButton>
          </Composer>
        </PreviewCard>
      </PreviewGrid>
    </PreviewShell>
  );
};

const PreviewShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PreviewCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PreviewTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const PreviewDescription = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${theme.colors.grey1};
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: stretch;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewCard = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 380px;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid ${theme.colors.grey2};
`;

const CardTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #111827;

  .anticon {
    color: ${theme.colors.green1};
  }
`;

const EditableTag = styled.span`
  font-size: 12px;
  color: ${theme.colors.grey1};
`;

const DraftArea = styled(Input.TextArea)`
  flex: 1;
  border: none !important;
  box-shadow: none !important;
  padding: 16px !important;
  font-size: 15px;
  line-height: 1.55;
  resize: none;

  &:focus,
  &:hover {
    border: none !important;
    box-shadow: none !important;
  }
`;

const CardMeta = styled.div`
  padding: 10px 16px 14px;
  font-size: 12px;
  line-height: 1.45;
  color: ${theme.colors.grey1};
  border-top: 1px solid ${theme.colors.grey2};
`;

const StopOptOutRow = styled.div`
  padding: 0 16px 14px;
`;

const StopOptOutCheckbox = styled.div`
  .ant-checkbox-wrapper {
    display: flex;
    align-items: flex-start;
    white-space: normal;
    line-height: 1.5;
    font-size: 13px;
    color: #111827;
  }

  .ant-checkbox {
    margin-top: 3px;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${theme.colors.green1} !important;
    border-color: ${theme.colors.green1} !important;
  }

  .ant-checkbox:hover .ant-checkbox-inner,
  .ant-checkbox-wrapper:hover .ant-checkbox-inner,
  .ant-checkbox-input:focus + .ant-checkbox-inner {
    border-color: ${theme.colors.green1} !important;
  }
`;

const Thread = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  overflow-y: auto;
  max-height: 280px;
  background: ${theme.colors.grey4};
`;

const AgentBubble = styled.div`
  align-self: flex-start;
  max-width: 92%;
  padding: 10px 12px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid ${theme.colors.grey2};
  color: #111827;
  font-size: 13px;
  line-height: 1.5;
`;

const UserBubble = styled.div`
  align-self: flex-end;
  max-width: 86%;
  padding: 8px 12px;
  border-radius: 16px;
  background: ${theme.colors.green2};
  color: ${theme.colors.green1};
  font-size: 13px;
  line-height: 1.4;
  font-weight: 500;
`;

const SuggestionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
`;

const SuggestionChip = styled.button`
  border: 1px solid ${theme.colors.green1};
  background: ${theme.colors.green2};
  color: ${theme.colors.green1};
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 13px;
  line-height: 1.3;
  cursor: pointer;
`;

const Typing = styled.div`
  align-self: flex-start;
`;

const Composer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid ${theme.colors.grey2};
  background: #fff;
`;

const ComposerInput = styled(Input)`
  flex: 1;
  height: 40px;
  border-radius: 8px;
`;

const SendButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 10px;
  background: ${theme.colors.green1};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

export default CampaignMessagePreview;
